import React, { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Flag, Briefcase, LifeBuoy, Users, CircleAlert } from "lucide-react";
import OnboardingSectionCard from "../../OnboardingSectionCard";
import { ChipInput } from "@/shared/components/ui/Input/chipInput";
import CompanySizeCard from "./CompanySizeCard";
import SmallBusinessIcon from "@/assets/svg/SmallBusinessIcon";
import MediumBusinessIcon from "@/assets/svg/MediumBusinessIcon";
import LargeBusinessIcon from "@/assets/svg/LargeBusinessIcon";
import { EnterpriseBusinessIcon } from "@/assets/svg/EnterpriseBusinessIcon";
import { defineAudienceSchema } from "../../../../../schema/OnBoarding";
import { getZodErrorMessages } from "@/features/campaigns/components/UserCampaigns/components/wizardValidationUtils";
import {
  countryOptions,
  roleOptions,
  targetIndustriesOptions,
} from "@/shared/utils/utils";
import { normalizeCompanySizeList } from "@/shared/utils/onboardingConfig";
import { useMainStore } from "@/stores";
import useUnsavedChangesWarning from "@/shared/hooks/useUnsavedChangesWarning";
import CommonInput from "@/shared/components/ui/Input/CommonInput";

const DefineAudienceForm = ({
  registerSubmitHandler,
  onDataChange,
  initialValues = null,
  suppressUnsavedWarning = false,
}) => {
  const { companyProfile, setCompanyProfile } = useMainStore();

  const {
    handleSubmit,
    watch,
    setValue,
    trigger,
    getValues,
    formState: { errors, isDirty },
  } = useForm({
    mode: "all",
    resolver: zodResolver(defineAudienceSchema),
    defaultValues: {
      company_size: normalizeCompanySizeList(initialValues?.companySize),
      target_industries: initialValues?.targetIndustries || [],
      roles: initialValues?.roles || [],
      business_locations: initialValues?.businessLocations || [],
      ideal_customer: initialValues?.idealCustomer || "",
    },
  });

  useUnsavedChangesWarning(suppressUnsavedWarning ? false : isDirty);

  useEffect(() => {
    if (initialValues?.idealCustomer && !getValues("ideal_customer")) {
      setValue("ideal_customer", initialValues.idealCustomer);
    }
  }, [initialValues?.idealCustomer, setValue, getValues]);

  const formvalues = watch([
    "company_size",
    "target_industries",
    "roles",
    "business_locations",
    "ideal_customer",
  ]);

  const [
    companySize,
    targetIndustries,
    roles,
    businessLocations,
    idealCustomer,
  ] = formvalues;

  const formValuesRef = useRef({
    companySize,
    targetIndustries,
    roles,
    businessLocations,
    idealCustomer,
  });

  useEffect(() => {
    formValuesRef.current = {
      companySize,
      targetIndustries,
      roles,
      businessLocations,
      idealCustomer,
    };

    if (onDataChange) {
      onDataChange({
        companySize,
        targetIndustries,
        roles,
        businessLocations,
        idealCustomer,
      });
    }
  }, [
    companySize,
    targetIndustries,
    roles,
    businessLocations,
    idealCustomer,
    onDataChange,
  ]);

  useEffect(() => {
    if (registerSubmitHandler) {
      const submitHandler = async () => {
        const isValid = await trigger();
        if (!isValid) {
          return {
            success: false,
            fieldErrors: getZodErrorMessages(defineAudienceSchema, getValues()),
          };
        }
        const {
          companySize,
          targetIndustries,
          roles,
          businessLocations,
          idealCustomer,
        } = formValuesRef.current;
        setCompanyProfile({
          ...companyProfile,
          target_industries: targetIndustries,
          roles: roles,
          business_locations: businessLocations,
          company_size: companySize,
          ideal_customer: idealCustomer,
        });

        return {
          success: true,
          data: {
            companySize,
            targetIndustries,
            roles,
            businessLocations,
            idealCustomer,
          },
        };
      };
      registerSubmitHandler(submitHandler);
    }
  }, [registerSubmitHandler, trigger, getValues, setCompanyProfile, companyProfile]);

  // useEffect(() => {
  //   const hasSuggestions =
  //     companyProfile?.target_industries_suggestions &&
  //     companyProfile?.roles_suggestions &&
  //     companyProfile?.business_locations_suggestions &&
  //     companyProfile?.business_interests_suggestions;

  //   if (isAISelected && hasSuggestions) {
  //     reset({
  //       company_size: companyProfile.company_size ?? "small",
  //       target_industries: companyProfile.target_industries_suggestions,
  //       roles: companyProfile.roles_suggestions,
  //       business_locations: companyProfile.business_locations_suggestions,
  //       business_interest: companyProfile.business_interests_suggestions,
  //     });
  //   }
  // }, [isAISelected, companyProfile, reset]);

  // useEffect(() => {
  //   console.log("AI Selected? ", isAISelected);
  //   console.log("CompanyProfile in form:", companyProfile);
  // }, [isAISelected]);

  // const onSubmit = async () => {
  //   const isValid = await trigger();
  //   if (!isValid) return false;

  //   const updatedProfile = {
  //     company_size: companySize,
  //     target_industries: targetIndustries,
  //     roles,
  //     business_locations: businessLocations,
  //   };

  //   setCompanyProfile({
  //     ...companyProfile,
  //     ...updatedProfile,
  //   });

  //   return true;
  // };

  const normalizedCompanySize = normalizeCompanySizeList(companySize);

  const BUSINESS_SIZES = [
    {
      label: "Small Business",
      subLabel: "1–50 employees",
      value: "1-50",
      icon: (
        <SmallBusinessIcon selected={normalizedCompanySize.includes("1-50")} />
      ),
    },
    {
      label: "Medium Business",
      subLabel: "51–200 employees",
      value: "51-200",
      icon: (
        <MediumBusinessIcon
          selected={normalizedCompanySize.includes("51-200")}
        />
      ),
    },
    {
      label: "Large Business",
      subLabel: "201–500 employees",
      value: "201-500",
      icon: (
        <LargeBusinessIcon
          selected={normalizedCompanySize.includes("201-500")}
        />
      ),
    },
    {
      label: "Enterprise",
      subLabel: "500+ employees",
      value: "500+",
      icon: (
        <EnterpriseBusinessIcon
          selected={normalizedCompanySize.includes("500+")}
        />
      ),
    },
  ];

  const toggleCompanySize = (value) => {
    const current = normalizeCompanySizeList(companySize);

    const updated = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];

    setValue("company_size", updated, { shouldValidate: true });
  };

  return (
    <form
      className="space-y-8"
      onSubmit={handleSubmit(() => {
        const { painPointsSolved, valueProposition, salesTrigger } =
          formValuesRef.current;
        setCompanyProfile({
          ...companyProfile,
          pain_points: painPointsSolved,
          value_proposition: valueProposition,
          sales_trigger: salesTrigger,
        });
      })}
    >
      {/* Company Size */}
      <OnboardingSectionCard
        icon={<CircleAlert className=" text-gray-500" size={20} />}
        title="Company Size"
      >
        <div className="flex flex-wrap border border-gray-300 dark:border-gray-800 p-3 rounded-lg gap-4 bg-gray-50 dark:bg-gray-900/50">
          {BUSINESS_SIZES.map(({ label, subLabel, value, icon }) => (
            <CompanySizeCard
              key={value}
              label={label}
              subLabel={subLabel}
              value={normalizedCompanySize}
              icon={icon}
              isChecked={normalizedCompanySize.includes(value)}
              handleCheck={() => toggleCompanySize(value)}
            />
          ))}
        </div>
        {errors.company_size && (
          <p className="text-red-500 text-xs mt-1">
            {errors.company_size.message}
          </p>
        )}
      </OnboardingSectionCard>

      {/* Target Industries */}
      <OnboardingSectionCard
        icon={<LifeBuoy className=" text-gray-500" size={20} />}
        title="Target Industries"
      >
        <ChipInput
          suggestions={companyProfile?.target_industries_suggestions}
          defaultChips={targetIndustries}
          placeholder="Add Target Industries"
          dropdownOptions={targetIndustriesOptions}
          onChange={(chips) =>
            setValue("target_industries", chips, { shouldValidate: true })
          }
          errors={errors.target_industries}
        />
      </OnboardingSectionCard>

      {/* Roles */}
      <OnboardingSectionCard
        icon={<Briefcase className=" text-gray-500" size={20} />}
        title="Roles"
      >
        <ChipInput
          defaultChips={roles}
          suggestions={companyProfile?.roles_suggestions}
          placeholder="Add Roles"
          dropdownOptions={roleOptions}
          onChange={(chips) =>
            setValue("roles", chips, { shouldValidate: true })
          }
          errors={errors.roles}
        />
      </OnboardingSectionCard>

      {/* Business Locations */}
      <OnboardingSectionCard
        icon={<Flag className=" text-gray-500" size={20} />}
        title="Location of Business"
      >
        <ChipInput
          defaultChips={businessLocations}
          suggestions={companyProfile?.business_locations_suggestions}
          placeholder="Add Locations"
          dropdownOptions={countryOptions}
          onChange={(chips) =>
            setValue("business_locations", chips, { shouldValidate: true })
          }
          errors={errors.business_locations}
        />
      </OnboardingSectionCard>

      {/* ideal customer */}
      <OnboardingSectionCard
        icon={<Users className=" text-gray-500" size={20} />}
        title="Ideal Customer (one-line description)"
      >
        <CommonInput
          placeholder="Describe your best-fit customer in one simple sentence.This helps AI personalize messaging and targeting"
          value={idealCustomer}
          onChange={(e) =>
            setValue("ideal_customer", e.target.value, { shouldValidate: true })
          }
          className="w-full"
          inputClassName="w-full px-3 py-2"
        />
        {errors.ideal_customer && (
          <p className="text-red-500 text-xs mt-1">
            {errors.ideal_customer.message}
          </p>
        )}
      </OnboardingSectionCard>

      {/* Business Interests */}
      {/* <OnboardingSectionCard
        icon={<Compass className="w-5 h-5 text-secondary" />}
        title="Type of Business Interest"
        description="What kind of opportunities are you looking for?"
      >
        <ChipInput
          defaultChips={businessInterests}
          suggestions={companyProfile?.business_interests_suggestions}
          placeholder="Add Business Interests"
          dropdownOptions={businessInterest}
          onChange={(chips) =>
            setValue("business_interest", chips, { shouldValidate: true })
          }
          errors={errors.business_interest}
        />
      </OnboardingSectionCard> */}
    </form>
  );
};

export default DefineAudienceForm;
